import { getEncryptParams } from 'src/keycloak';
import { decrypt3, decrypt_file_binary } from '../encryption';
import { lib } from 'crypto-js';
import logger from '../logger';
import { encryptBufferWithKey, convertWordArrayToUint8Array } from '../workers/encryptCore';
import { FileEncodingType } from '../constants';

function getStringLengthInBytes(inputString) {
  // Convert the length of the string to its byte representation
  const stringLength = Buffer.alloc(4); // Allocate 4 bytes
  stringLength.writeUInt32BE(inputString?.length, 0);
  return stringLength;
}

function stringToBytes(inputString) {
  return Buffer.from(inputString);
}

function stringToBytesAndWrite(inputString: string) {
  const lengthInBytes = getStringLengthInBytes(inputString);

  // Combine the byte representations
  const dataToWrite = Buffer.concat([
    lengthInBytes,
    stringToBytes(inputString)
  ]);

  return dataToWrite;
}

export function getEncryptedFile(
  arrayBufferData,
  fileName: string,
  fileType: string
) {
  const key = getEncryptParams()?.k;
  const encryptedBuffer = encryptBufferWithKey(arrayBufferData, key);
  return new File([encryptedBuffer], fileName, { type: fileType });
}

export function readFileAsBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = (event) => {
      reject(event.target.error);
    };
    reader.readAsArrayBuffer(file);
  });
}

export async function blobToBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = Buffer.from(reader.result as ArrayBuffer);
      resolve(buffer);
    };
    reader.onerror = () => {
      reject(new Error('Failed to convert Blob to Buffer'));
    };
    reader.readAsArrayBuffer(blob);
  });
}

export function readChunksFromBuffer(bufferData) {
  const chunks = [];
  let currentIndex = 0;
  while (currentIndex < bufferData.length) {
    const chunkLength = bufferData.readInt32BE(currentIndex);
    currentIndex += 4;
    const chunk = bufferData.slice(currentIndex, currentIndex + chunkLength);
    chunks.push(chunk);
    currentIndex += chunkLength;
  }
  return chunks;
}


export const downloadFileToSystem = (data, name) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const decryptFileData = async (bufferData, encoding?: FileEncodingType) => {
  let decryptedData;

  try {
    const key = getEncryptParams()?.k;
    // Use content_info.enc when present; for files without content_info, detect from the 0x01 version byte.
    const isBinaryFormat =
      encoding === 'binary' ||
      (encoding === undefined && bufferData.length > 0 && bufferData[0] === 0x01);
    const chunkData = isBinaryFormat ? bufferData.slice(1) : bufferData;
    const chunks = readChunksFromBuffer(chunkData);
    const decryptedChunks: lib.WordArray[] = [];

    for (const chunk of chunks) {
      let decrypted: lib.WordArray;
      if (isBinaryFormat) {
        decrypted = decrypt_file_binary(key, chunk);
      } else {
        decrypted = decrypt3(key, chunk.toString(), false) as lib.WordArray;
      }
      decryptedChunks.push(decrypted);
    }

    let combinedDecrypted = lib.WordArray.create();
    for (const x of decryptedChunks) {
      combinedDecrypted = combinedDecrypted.concat(x);
    }
    decryptedData = convertWordArrayToUint8Array(combinedDecrypted);
  } catch (err) {
    logger.error(err);
  }
  return decryptedData;
};
